import axios from "axios";
import type { Doctor, DoctorSlot } from "./doctorService";
import type { PublicBookAppointmentDto } from "./appointmentService";
import type { PublicBookingOptions } from "./clinicalSettingsService";

/**
 * API client for pages used without signing in (online booking). It never
 * sends the staff login token; it names the hospital with the public code
 * from the page link (?h=CODE), which the backend resolves to one hospital.
 */
const client = axios.create({
  baseURL: (import.meta as any).env?.VITE_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

let hospitalCode = "";
client.interceptors.request.use((config) => {
  if (hospitalCode) config.headers["X-Hospital-Code"] = hospitalCode;
  return config;
});

/** A hospital asking for a DaAFYA workspace; the backend sends it to Driventra CRM. */
export interface SignupRequest {
  hospital: string;
  name: string;
  email: string;
  phone: string;
  city?: string;
  size?: string;
  message?: string;
  captcha_token: string;
  captcha_answer: string;
  _gotcha?: string;
}

export const publicApi = {
  setHospitalCode(code: string) {
    hospitalCode = code.trim().toUpperCase();
  },

  async doctors(params: { active_only?: boolean; limit?: number } = {}): Promise<Doctor[]> {
    const { data } = await client.get("/doctors", { params });
    return data?.data || [];
  },

  async bookingOptions(): Promise<PublicBookingOptions> {
    const { data } = await client.get("/appointments/public/booking-options");
    return data?.data || data;
  },

  async availableSlots(doctorId: number, date: string): Promise<DoctorSlot[]> {
    const { data } = await client.get(`/doctors/${doctorId}/available-slots`, { params: { date } });
    return data?.data?.data || data?.data || [];
  },

  async captcha(): Promise<{ token: string; svg: string; expires_in: number }> {
    const { data } = await client.get("/public/captcha", { timeout: 10_000 });
    return data;
  },

  async sendBookingOtp(payload: { phone_number: string; captcha_token: string; captcha_answer: string }): Promise<{ expires_in: number; fallback_code?: string }> {
    const { data } = await client.post('/appointments/public/otp/send', payload);
    return data?.data || data;
  },

  async verifyBookingOtp(payload: { phone_number: string; code: string }): Promise<{ verification_token: string; patient_exists: boolean; expires_in: number }> {
    const { data } = await client.post('/appointments/public/otp/verify', payload);
    return data?.data || data;
  },

  async signupRequestsEnabled(): Promise<boolean> {
    const { data } = await client.get("/public/signup-requests", { timeout: 10_000 });
    return !!(data?.data ?? data)?.enabled;
  },

  async sendSignupRequest(payload: SignupRequest): Promise<void> {
    await client.post("/public/signup-requests", payload, { timeout: 20_000 });
  },

  async availableCalendar(doctorId: number, month: string): Promise<DoctorSlot[]> {
    const { data } = await client.get(`/doctors/${doctorId}/available-calendar`, { params: { month } });
    return data?.data || [];
  },

  async resumePayment(payment_resume_token: string, checkOnly = false) {
    const { data } = await client.post('/appointments/public/resume-payment', { payment_resume_token, check_only: checkOnly });
    return data?.data || data;
  },

  async book(dto: PublicBookAppointmentDto & { verification_token: string }) {
    const { data } = await client.post("/appointments/public/book", dto);
    return data;
  },
};

/** The backend's message for a failed public request, for showing to the patient. */
export function publicErrorMessage(error: any, fallback: string): string {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(", ");
  return message || fallback;
}
